/**
 * Danish Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const da: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Oversigt',
  'nav.funds': 'Fonde',
  'nav.investments': 'Investeringer',
  'nav.investees': 'Investeringsmodtagere',
  'nav.esg': 'ESG Gennemgang',
  'nav.compliance': 'Overholdelse',
  'nav.reports': 'Rapporter',
  'nav.settings': 'Indstillinger',
  'nav.users': 'Brugere',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indikatorer',
  'nav.taxonomy': 'EU-taksonomi',
  'nav.filings': 'Indberetninger',
  'nav.calendar': 'Kalender',
  'nav.submit': 'Indsend data',
  'nav.submissions': 'Mine indberetninger',
  'nav.benchmarks': 'Benchmarks',
  'nav.messages': 'Beskeder',
  'nav.portfolio': 'Portefølje',
  'nav.performance': 'Fondsresultater',
  'nav.esgSummary': 'ESG Resumé',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Effektmålinger',
  'nav.sdg': 'SDG-kortlægning',
  'nav.audit': 'Revisionsspor',
  'nav.snapshots': 'Datasnapshots',
  'nav.knowledgeGraph': 'Videngraf',
  'nav.geographic': 'Geografisk',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Assistent',
  'nav.tasks': 'Opgaver',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatorisk monitor',

  // Common Actions
  'action.create': 'Opret',
  'action.edit': 'Rediger',
  'action.delete': 'Slet',
  'action.save': 'Gem',
  'action.cancel': 'Annuller',
  'action.submit': 'Indsend',
  'action.approve': 'Godkend',
  'action.reject': 'Afvis',
  'action.download': 'Download',
  'action.export': 'Eksporter',
  'action.import': 'Importer',
  'action.filter': 'Filtrer',
  'action.search': 'Søg',
  'action.refresh': 'Opdater',
  'action.close': 'Luk',
  'action.back': 'Tilbage',
  'action.next': 'Næste',
  'action.previous': 'Forrige',
  'action.viewAll': 'Vis alle',
  'action.viewDetails': 'Vis detaljer',
  'action.generate': 'Generer',
  'action.signOut': 'Log ud',
  'action.addNew': 'Tilføj ny',
  'action.invite': 'Inviter',

  // Statuses
  'status.draft': 'Kladde',
  'status.submitted': 'Indsendt',
  'status.validated': 'Valideret',
  'status.approved': 'Godkendt',
  'status.rejected': 'Afvist',
  'status.pending': 'Afventer',
  'status.inProgress': 'I gang',
  'status.overdue': 'Forsinket',
  'status.compliant': 'Overholdt',
  'status.filed': 'Indberettet',
  'status.active': 'Aktiv',
  'status.inactive': 'Inaktiv',
  'status.closed': 'Lukket',
  'status.fundraising': 'Fundraising',
  'status.investing': 'Investering',
  'status.harvesting': 'Høst',
  'status.validating': 'Validerer',
  'status.review': 'Under gennemgang',
  'status.monitoring': 'Overvågning',
  'status.exiting': 'Afslutning',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Due Diligence',
  'status.disbursed': 'Udbetalt',
  'status.restructured': 'Omstruktureret',
  'status.matured': 'Udløbet',
  'status.written_off': 'Afskrevet',
  'status.exited': 'Afsluttet',
  'status.incomplete': 'Ufuldstændig',
  'status.open': 'Åben',
  'status.in_progress': 'I gang',
  'status.completed': 'Afsluttet',
  'status.cancelled': 'Annulleret',
  'status.accepted': 'Accepteret',
  'status.failed': 'Fejlet',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Porteføljeoversigt',
  'dashboard.complianceStatus': 'Overholdelsesstatus',
  'dashboard.collectionProgress': 'Indsamlingsfremskridt',
  'dashboard.activityFeed': 'Aktivitet',
  'dashboard.quickActions': 'Hurtige handlinger',
  'dashboard.investmentPipeline': 'Investeringspipeline',
  'dashboard.totalFunds': 'Fonde i alt',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktive investeringer',
  'dashboard.totalInvestees': 'Investeringsmodtagere',
  'dashboard.recentActivity': 'Seneste aktivitet',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Samlet forpligtet',
  'lp.capitalCalled': 'Kaldt kapital',
  'lp.distributed': 'Distribueret',
  'lp.netPosition': 'Nettoposition',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fondsresultater',
  'lp.esgSummary': 'ESG Resumé',
  'lp.reportLibrary': 'Rapportbibliotek',
  'lp.taxonomyAligned': 'Taksonomi-tilpasset',
  'lp.paiCoverage': 'PAI Dækning',
  'lp.portfolioPerformance': 'LP Porteføljeresultater',

  // Fund Labels
  'fund.name': 'Fondnavn',
  'fund.code': 'Fondkode',
  'fund.vintage': 'Grundlæggelsesår',
  'fund.size': 'Fondstørrelse',
  'fund.targetSize': 'Målstørrelse',
  'fund.currency': 'Valuta',
  'fund.strategy': 'Strategi',
  'fund.status': 'Status',
  'fund.sfdrCategory': 'SFDR Kategori',
  'fund.geographyFocus': 'Geografisk fokus',
  'fund.sectorFocus': 'Sektorfokus',

  // Form Labels
  'form.name': 'Navn',
  'form.email': 'E-mail',
  'form.country': 'Land',
  'form.amount': 'Beløb',
  'form.currency': 'Valuta',
  'form.date': 'Dato',
  'form.description': 'Beskrivelse',
  'form.notes': 'Noter',
  'form.type': 'Type',
  'form.period': 'Periode',
  'form.year': 'År',
  'form.required': 'Påkrævet',
  'form.optional': 'Valgfrit',

  // Table Headers
  'table.fund': 'Fond',
  'table.commitment': 'Forpligtelse',
  'table.called': 'Kaldt',
  'table.distributed': 'Distribueret',
  'table.tvpi': 'TVPI',
  'table.status': 'Status',
  'table.regulation': 'Regulering',
  'table.filingType': 'Indberetningstype',
  'table.reportingPeriod': 'Periode',
  'table.createdAt': 'Oprettet',
  'table.actions': 'Handlinger',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU-taksonomi',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Datakomplethed',
  'compliance.nextDeadline': 'Næste frist',

  // Reports
  'report.quarterly': 'Kvartalsrapport',
  'report.annual': 'Årsrapport',
  'report.paiStatement': 'PAI Erklæring',
  'report.taxonomyReport': 'Taksonomirapport',
  'report.generatedAt': 'Genereret',
  'report.version': 'Version',

  // General
  'general.loading': 'Indlæser...',
  'general.noData': 'Ingen tilgængelige data',
  'general.error': 'Der opstod en fejl',
  'general.retry': 'Prøv igen',
  'general.total': 'I alt',
  'general.page': 'Side',
  'general.of': 'af',
  'general.showing': 'Viser',
  'general.results': 'resultater',
  'general.language': 'Sprog',
  'general.english': 'Engelsk',
  'general.french': 'Fransk',

  // Tour
  'tour.takeTour': 'Tag en rundvisning',
  'tour.skip': 'Spring over',
  'tour.next': 'Næste',
  'tour.prev': 'Forrige',
  'tour.finish': 'Afslut',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigation',
  'tour.sidebarContent': 'Brug sidebjælken til at navigere mellem porteføljestyring, ESG-dataindsamling, compliance, rapporter og analyseværktøjer.',
  'tour.kpisTitle': 'Porteføljeoversigt',
  'tour.kpisContent': 'Disse kort viser din portefølje med et overblik — samlede fonde, AUM, aktive investeringer og antal investeringsmodtagere.',
  'tour.actionsTitle': 'Hurtige handlinger',
  'tour.actionsContent': 'Opret fonde, tilføj investeringer, inviter modtagere og generer rapporter med ét klik.',
  'tour.languageTitle': 'Sprog',
  'tour.languageContent': 'Skift mellem 24 officielle EU-sprog. Hele grænsefladen oversættes øjeblikkeligt.',
  'tour.aiTitle': 'AI-assistent',
  'tour.aiContent': 'Stil spørgsmål om din portefølje når som helst. AI-assistenten har fuld kontekst om dine fonde og investeringer.',
  'action.createFund': 'Opret fond',
  'action.editFund': 'Rediger fond',
  'action.addInvestment': 'Tilføj investering',
  'action.addInvestee': 'Tilføj modtager',
  'action.editInvestee': 'Rediger modtager',
  'action.createTemplate': 'Opret skabelon',
  'action.createSubmission': 'Opret indsendelse',
  'action.createFiling': 'Opret indberetning',
  'action.generateReport': 'Generer rapport',
  'action.inviteUser': 'Inviter bruger',
  'action.sendInvitation': 'Send invitation',
  'action.saveChanges': 'Gem ændringer',
  'form.fundName': 'Fondsnavn',
  'form.fundCode': 'Fondskode',
  'form.sfdrCategory': 'SFDR Kategori',
  'form.baseCurrency': 'Basisvaluta',
  'form.targetSize': 'Målstørrelse',
  'form.vintageYear': 'Grundlæggelsesår',
  'form.geographyFocus': 'Geografisk fokus',
  'form.sectorFocus': 'Sektorfokus',
  'form.investmentCode': 'Investeringskode',
  'form.instrumentType': 'Instrumenttype',
  'form.maturityDate': 'Udløbsdato',
  'form.sector': 'Sektor',
  'form.countryCode': 'Landekode',
  'form.entityType': 'Enhedstype',
  'form.region': 'Region',
  'form.staffCount': 'Antal ansatte',
  'form.borrowers': 'Låntagere',
  'form.contactName': 'Kontaktnavn',
  'form.contactEmail': 'Kontakt-e-mail',
  'form.templateName': 'Skabelonnavn',
  'form.templateType': 'Skabelontype',
  'form.indicators': 'Indikatorer',
  'form.reportingPeriod': 'Rapporteringsperiode',
  'form.reportingYear': 'Rapporteringsår',
  'form.regulation': 'Regulering',
  'form.filingType': 'Indberetningstype',
  'form.reportType': 'Rapporttype',
  'form.emailAddress': 'E-mailadresse',
  'form.role': 'Rolle',
  'form.selectFund': 'Vælg fond...',
  'form.selectInvestee': 'Vælg modtager...',
  'table.code': 'Kode',
  'table.investee': 'Modtager',
  'table.instrument': 'Instrument',
  'table.amount': 'Beløb',
  'table.currency': 'Valuta',
  'table.maturity': 'Udløb',
  'table.name': 'Navn',
  'table.country': 'Land',
  'table.type': 'Type',
  'table.period': 'Periode',
  'table.completion': 'Fuldførelse',
  'table.submitted': 'Indsendt',
  'table.version': 'Version',
  'table.indicators': 'Indikatorer',
  'table.dueDate': 'Forfaldsdato',
  'table.generated': 'Genereret',
  'table.email': 'E-mail',
  'table.role': 'Rolle',
  'error.createFailed': 'Oprettelse mislykkedes. Prøv igen.',
  'error.updateFailed': 'Opdatering mislykkedes. Prøv igen.',
  'error.loadFailed': 'Indlæsning af data mislykkedes.',
};
